import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { withStyles, createStyles, Theme, WithStyles } from "@material-ui/core";
import { withRouter } from "react-router-dom";

import SnackbarCore, { SnackbarOrigin } from "@material-ui/core/Snackbar";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import amber from "@material-ui/core/colors/amber";
import green from "@material-ui/core/colors/green";

import { StoreState } from "../reducers/root-reducer";
import { setSnackbarMessage } from "../actions/store/snackbar-actions";
import { SnackbarVariant, SnackbarClickPayload } from "../actions/store/type";
import { Inquiry } from "../api/inquiries";
import { InjectedRouterProps } from "../router/props";
import {
	changeInquiryListDateRange,
	changeHighlightedInquiry
} from "../actions/store/inquiry-list-actions";

interface State {
	isVisible: boolean;
	message: string;
	variant: SnackbarVariant;
}

export interface SnackbarProps {}

interface ConnectedSnackbarProps extends WithStyles<typeof styles> {
	message: string;
	variant: SnackbarVariant;
	layout: "desktop" | "tablet" | "phone";
	clickPayload: SnackbarClickPayload<any>;
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
	changeInquiryListDateRange: (startDate: Date, endDate: Date) => void;
	changeHighlightedInquiry: (id: string) => void;
}

type Props = SnackbarProps & ConnectedSnackbarProps & InjectedRouterProps;

export class Snackbar extends React.Component<Props, State> {
	public state: State = {
		isVisible: false,
		message: null,
		variant: "info"
	};

	private timer: number;

	constructor(props: Props) {
		super(props);

		this.setMessage = this.setMessage.bind(this);
		this.handleClose = this.handleClose.bind(this);
	}

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		if (this.props.message !== nextProps.message) {
			if (this.props.message !== null && nextProps.message !== null) {
				this.setState(
					{
						isVisible: false
					},
					() => {
						clearTimeout(this.timer);
						this.timer = setTimeout(
							() =>
								this.setMessage(
									nextProps.message,
									nextProps.variant
								),
							300
						);
					}
				);
			} else if (nextProps.message !== null) {
				this.setMessage(nextProps.message, nextProps.variant);
			} else {
				this.setState({
					isVisible: false
				});
			}

			return false;
		}

		return true;
	}

	handleClose(event?: React.MouseEvent) {
		if (event) {
			event.stopPropagation();
		}

		this.props.setSnackbarMessage(null);
	}

	handleInquiryMessageClick = (data: Inquiry) => {
		this.props.history.push("/inquiry-list");

		const startDate = new Date(data.date);
		const endDate = new Date(data.date);

		startDate.setHours(0, 0, 0, 0);
		endDate.setHours(23, 59, 59, 999);

		this.props.changeInquiryListDateRange(startDate, endDate);
		this.props.changeHighlightedInquiry(data.id);

		this.handleClose();
	};

	handleClick = () => {
		const { clickPayload } = this.props;

		if (clickPayload) {
			switch (clickPayload.type) {
				case "inquiry":
					this.handleInquiryMessageClick(clickPayload.data);
			}
		}
	};

	setMessage(message: string, variant: SnackbarVariant) {
		this.setState({
			message,
			variant,
			isVisible: true
		});
	}

	render() {
		const { classes, layout } = this.props;

		const alignment: SnackbarOrigin =
			layout === "phone"
				? {
						vertical: "bottom",
						horizontal: "center"
				  }
				: {
						vertical: "top",
						horizontal: "right"
				  };

		return (
			<SnackbarCore
				className={classNames(classes.snackbar)}
				anchorOrigin={alignment}
				open={this.state.isVisible}
				autoHideDuration={6000}
				onClose={this.handleClose}
				onClick={this.handleClick}
			>
				<SnackbarContent
					className={classes[this.state.variant]}
					message={
						<span
							dangerouslySetInnerHTML={{
								__html: this.state.message
							}}
						/>
					}
					action={
						<IconButton
							key="close"
							aria-label="Close"
							color="inherit"
							className={classes.close}
							onClick={this.handleClose}
						>
							<CloseIcon />
						</IconButton>
					}
				/>
			</SnackbarCore>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	message: state.snackbarMessage,
	variant: state.snackbarVariant,
	layout: state.layout,
	clickPayload: state.snackbarClickPayload
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant)),
	changeInquiryListDateRange: (startDate: Date, endDate: Date) =>
		dispatch(changeInquiryListDateRange(startDate, endDate)),
	changeHighlightedInquiry: (id: string) =>
		dispatch(changeHighlightedInquiry(id))
});

const styles = (theme: Theme) =>
	createStyles({
		snackbar: {
			userSelect: "none"
		},
		info: {},
		warning: {
			backgroundColor: amber[700]
		},
		error: {
			backgroundColor: theme.palette.error.dark
		},
		success: {
			backgroundColor: green[600]
		},
		close: {
			width: theme.spacing.unit * 4,
			height: theme.spacing.unit * 4
		}
	});

export const ConnectedSnackbar = withRouter(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(Snackbar)
	)
);
